import React from 'react'
import {log} from '@vanguard/logger'

import {api} from '../services/api'
import {PhotoRecognitionAttempts} from '../types'

// Values match the codebase-wide fetch-status vocabulary used by sibling hooks
// ('pending'/'rejected' rather than 'loading'/'error').
export type FetchStatus = 'idle' | 'pending' | 'resolved' | 'rejected'

interface Params {
  photoId: number
  clientId: number
  allScans: boolean
  // Gate the fetch so the recognition-attempts call is never fired while the
  // owning modal is closed (mirrors the previous `if (!isOpen) return` guard).
  enabled: boolean
}

export interface RecognitionAttemptsState {
  status: FetchStatus
  data: PhotoRecognitionAttempts | null
  nameMap: Map<number, string>
  errorMessage: string | null
}

/**
 * Owns the FR recognition-attempts fetch and the follow-on candidate-name
 * resolution state machine for a single photo. Returns the render state the
 * FaceConfidenceModal consumes.
 *
 * Behavior contract (kept identical to the modal's original inline effect):
 * - A `cancelled` flag stale-guards every `set*` so a resolution that lands
 *   after unmount / dependency change is discarded.
 * - Name resolution is a display enhancement: a failure falls back to an empty
 *   name map (the UI shows "Person <id>") plus a breadcrumb, and never flips
 *   the modal into the error state.
 */
export const useRecognitionAttempts = ({
  photoId,
  clientId,
  allScans,
  enabled,
}: Params): RecognitionAttemptsState => {
  const [status, setStatus] = React.useState<FetchStatus>('idle')
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null)
  const [data, setData] = React.useState<PhotoRecognitionAttempts | null>(null)
  const [nameMap, setNameMap] = React.useState<Map<number, string>>(
    () => new Map(),
  )

  React.useEffect(() => {
    if (!enabled) return undefined

    let cancelled = false
    setStatus('pending')
    setErrorMessage(null)
    setNameMap(new Map())

    const fetchData = async (): Promise<void> => {
      const response = await api.faces.getRecognitionAttempts({
        photoId,
        clientId,
        allScans,
      })

      if (cancelled) return

      if (response.result !== 'success') {
        setData(null)
        setErrorMessage(response.error.message)
        setStatus('rejected')
        log.breadcrumb('Failed to load facial recognition confidence', {
          photoId,
        })
        return
      }

      setData(response.data)
      setStatus('resolved')

      // The recognition-attempts endpoint returns only personIds; resolve
      // display names via the persons GetNames batch wrapper (client-scoped,
      // de-dupes and chunks the ids internally).
      const personIds = response.data.faces.flatMap((face) =>
        face.candidates
          .map((candidate) => candidate.personId)
          .filter((id): id is number => id !== null),
      )

      if (personIds.length === 0) return

      const namesResponse = await api.persons.getNames({
        clientID: clientId,
        ids: personIds,
      })

      if (cancelled) return

      if (namesResponse.result === 'success') {
        setNameMap(namesResponse.data)
      } else {
        log.breadcrumb('Failed to resolve recognition candidate names', {
          photoId,
        })
      }
    }

    fetchData()

    return (): void => {
      cancelled = true
    }
  }, [enabled, photoId, clientId, allScans])

  return {status, data, nameMap, errorMessage}
}
